import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { getApiUrl } from '../helpers/rest-utils';
import { concatMap, EMPTY, map, Observable, of, tap } from 'rxjs';
import { Agent, AgentConfigOptions, AgentData, Booking, Configuration, CrawledUrl, CustomQuestion, MessageScenario, ScrapeStatus, TestCallQuestionsStatus, TrainingFaq, TrainingFile, TrainingStatus } from './agent';
import { UserService } from '../user/user.service';
import { PhoneNumber } from '../phone/phone';
import { Business, BusinessInformation } from '../business/business';
import { merge } from '@rallycommerce/common/utils.js';
import { SessionService } from '../session.service';
import { CallRateLimitService } from '../call-rate-limit/call-rate-limit.service';

@Injectable({
  providedIn: 'root'
})
export class AgentService {
  config: AgentConfigOptions;
  files: TrainingFile[] = [];
  faqs: TrainingFaq[] = [];
  customQuestions: CustomQuestion[] = [];

  constructor(private http: HttpClient, private user: UserService, private session: SessionService, private callRateLimits: CallRateLimitService) {
    this.session.terminate.subscribe(() => {
      this.files = [];
      this.faqs = [];
      this.customQuestions = [];
    });
  }

  // Derived from the embedded scenarios on AgentResource so the guided-setup
  // training poll (which only re-reads /agent) stays in sync without a
  // separate cache that getMessageScenarios used to maintain.
  get messageScenarios(): MessageScenario[] {
    return this.primary?.scenarios ?? [];
  }

  get businessId(): string {
    return this.user.activeBusiness?.id;
  }

  get business(): Business {
    return this.primary?.business;
  }

  get configuration(): Configuration {
    return this.primary?.configuration;
  }

  // The two dynamic questions, or none when the static list should show
  // (not generated, still pending, or the model wrote no usable questions).
  get testCallQuestions(): string[] {
    const testCallQuestions = this.configuration?.testCallQuestions;
    if (testCallQuestions?.status !== TestCallQuestionsStatus.Ready) {
      return [];
    }
    const questions = (testCallQuestions.questions ?? []).filter(question => question?.trim());
    return questions.length === 2 ? questions : [];
  }

  get isTestCallQuestionsPending(): boolean {
    return this.configuration?.testCallQuestions?.status === TestCallQuestionsStatus.Pending;
  }

  get businessInformation(): BusinessInformation {
    return this.primary?.business?.information;
  }

  get isTrainingInProgress(): boolean {
    return this.primary?.trainingStatus === TrainingStatus.InProgress;
  }

  get isTrainingFailed(): boolean {
    return this.primary?.trainingStatus === TrainingStatus.Failed;
  }

  get isTrainStepCompleted(): boolean {
    return this.primary?.configuration?.trainStepCompleted;
  }

  get isCustomizeStepCompleted(): boolean {
    return this.primary?.configuration?.customizeStepCompleted;
  }

  get isTestStepCompleted(): boolean {
    return this.primary?.configuration?.testStepCompleted;
  }

  get isLaunchStepViewed(): boolean {
    return this.primary?.configuration?.launchStepViewed;
  }

  get phoneNumber(): string {
    return this.primary?.phoneNumbers?.find(p => p.isPrimary)?.number;
  }

  get isTransferCallsEnabled(): boolean {
    return this.primary?.configuration?.hasTransferCallsEnabled;
  }

  get primary(): Agent {
    return this.user.agent;
  }

  get isBlandProvider(): boolean {
    return +this.primary?.providerId === 3;
  }

  get isVapiProvider(): boolean {
    return +this.primary?.providerId === 1;
  }

  get isLiveKitProvider(): boolean {
    return +this.primary?.providerId === 4;
  }

  retrainAgent(url: string = null, placeId: string = null, sourceUrls: { url: string, title: string }[] = null): Observable<any> {
    const data: any = { url, placeId };
    if (sourceUrls) {
      data.source_urls = sourceUrls;
    }
    return this.http.post(`${getApiUrl()}agents/${this.primary.id}/retrain`, data, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  discoverPages(url: string): Observable<CrawledUrl[]> {
    return this.http.get(`${getApiUrl()}pages/discover`, {
      params: { url },
      headers: { 'X-Business-ID': this.user.activeBusiness?.id }
    }).pipe(
      map((response: any) => (response?.data || []).map(p => ({
        url: p.url,
        title: p.title,
        active: true
      })))
    );
  }

  createAgent(data?: AgentData): Observable<Agent> {
    return this.http.post(`${getApiUrl()}agents`, { title: 'Rosie', "configuration": { voice: "woman", tone: "casual" } }, { headers: { 'X-Business-ID': this.user.activeBusiness.id } })
      .pipe(
        map((res: any) => res.data),
        tap((agent: Agent) => {
          const user = this.user.userData;
          user.agent = agent;
          this.user.refreshUser(user);
        }));
  }

  getAgent(omitAgentStateRefresh = false): Observable<Agent> {
    const activeBusinessId = this.user.activeBusiness?.id;
    if (!activeBusinessId) {
      // No business in memory — e.g. a background poll firing after logout has
      // dropped the cached user. There is nothing to fetch, and an undefined
      // header value makes HttpClient throw. Complete silently instead.
      return EMPTY;
    }
    return this.http.get(`${getApiUrl()}agent`, { headers: { 'X-Business-ID': activeBusinessId } })
      .pipe(
        map((res: any) => res.data),
        tap((agent: Agent) => {
          if (!omitAgentStateRefresh && activeBusinessId === this.user.activeBusiness?.id) {
            const user = this.user.userData;
            user.agent = agent;
            user.business = agent.business;
            this.user.refreshUser(user);
            this.callRateLimits.syncFromAgent(agent);
          }
        }));
  }

  refreshAgent(partialAgent) {
    const user = this.user.userData;
    user.agent = merge(user.agent, partialAgent);
    this.user.refreshUser(user);
  }

  updateAgent(agent: Agent) {
    const activeBusinessId = this.user.activeBusiness?.id;
    return this.http.put(`${getApiUrl()}agents/${this.primary.id}`, agent, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        map((res: any) => res.data),
        tap((agent: Agent) => {
          if (activeBusinessId === this.user.activeBusiness?.id) {
            const user = this.user.userData;
            user.agent = agent;
            this.user.refreshUser(user);
            this.callRateLimits.syncFromAgent(agent);
          }
        }));
  }

  updateAgentConfiguration(config: { configuration: Configuration }, omitAgentStateRefresh = false): Observable<any> {
    const activeBusinessId = this.user.activeBusiness?.id;
    return this.http.patch(`${getApiUrl()}agents/${this.primary.id}/configuration`, config, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        map((res: any) => res.data),
        concatMap((configuration: Configuration) => {
          if (!omitAgentStateRefresh && activeBusinessId === this.user.activeBusiness?.id) {
            return this.getAgent();
          }
          return of(configuration);
        })
      );
  }

  reissuePhoneNumber() {
    return this.http.post(`${getApiUrl()}businesses/${this.user.activeBusiness?.id}/phone-numbers/assign`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  getConfigOptions(): Observable<AgentConfigOptions> {
    return this.http.get(`${getApiUrl()}agent/configuration`)
      .pipe(map((res: any) => {
        return { toneOptions: res?.data?.tone, voiceOptions: res?.data?.voice, languages: res?.data?.languages, backgroundNoiseOptions: res?.data?.backgroundNoise, voiceSpeedOptions: res?.data?.voiceSpeedOptions || [], nameFormatOptions: res?.data?.nameFormat, holdMusicOptions: res?.data?.holdMusicOptions || [], voicePreviewText: res?.data?.voicePreviewText || '' };
      }), tap((configOptions: AgentConfigOptions) => { this.config = configOptions; }));
  }

  getFiles(): Observable<TrainingFile[]> {
    return this.http.get(`${getApiUrl()}files`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.data), tap((files) => this.files = files || [])) as Observable<TrainingFile[]>;
  }

  updateFaqs(faqs: TrainingFaq[]): Observable<TrainingFaq[]> {
    return this.http.post(`${getApiUrl()}faqs/batch`, { faqs }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  updateCustomQuestions(questions: CustomQuestion[]): Observable<CustomQuestion[]> {
    return this.http.post(`${getApiUrl()}agent/${this.primary.id}/questions/batch`, { questions }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  getFaqs(): Observable<TrainingFaq[]> {
    return this.http.get(`${getApiUrl()}faqs`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.data), tap((faqs) => this.faqs = faqs || [])) as Observable<TrainingFaq[]>;
  }

  getCustomQuestions(): Observable<CustomQuestion[]> {
    return this.http.get(`${getApiUrl()}agent/${this.primary.id}/questions`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.data), tap((questions) => this.customQuestions = questions || [])) as Observable<CustomQuestion[]>;
  }

  /**
   * Get message taking scenarios for the agent.
   * GET /agent/{agent}/scenarios
   */
  getMessageScenarios(): Observable<MessageScenario[]> {
    if (!this.primary?.id) { return of([] as MessageScenario[]); }
    return this.http.get(`${getApiUrl()}agent/${this.primary.id}/scenarios`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        map((response: any) => response?.data || []),
        tap((scenarios: MessageScenario[]) => {
          // Mirror customQuestions/faqs' direct-assign cache pattern, but on
          // the embedded agent property so the messageScenarios getter stays
          // in sync. Can't use refreshAgent here — lodash merge on arrays
          // merges by index and would leave stale items after a delete.
          if (this.primary) { this.primary.scenarios = scenarios || []; }
        }),
      ) as Observable<MessageScenario[]>;
  }

  /**
   * Batch upsert (full set) of message scenarios.
   * POST /agent/{agent}/scenarios
   */
  saveMessageScenarios(scenarios: MessageScenario[]): Observable<MessageScenario[]> {
    return this.http.post(`${getApiUrl()}agent/${this.primary.id}/scenarios`, { scenarios }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data || []));
  }

  /**
   * Promote a suggested scenario into a real scenario row. Server splices the
   * matching entry out of configuration.suggested_scenarios in the same
   * transaction, so on success we mutate the local copy to match.
   * POST /agent/{agent}/scenarios/from-suggestion
   */
  addScenarioFromSuggestion(title: string, text: string): Observable<MessageScenario> {
    return this.http.post(`${getApiUrl()}agent/${this.primary.id}/scenarios/from-suggestion`, { title, text }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        map((response: any) => response?.data as MessageScenario),
        tap((scenario) => {
          if (!this.primary) { return; }
          const config = this.primary.configuration || ({} as any);
          const stash = Array.isArray(config.suggestedScenarios) ? config.suggestedScenarios : [];
          let spliced = false;
          this.primary.configuration = {
            ...config,
            suggestedScenarios: stash.filter((entry: any) => {
              if (!spliced && entry?.title === title && entry?.text === text) { spliced = true; return false; }
              return true;
            }),
          };
          this.primary.scenarios = [...(this.primary.scenarios || []), scenario];
        }),
      ) as Observable<MessageScenario>;
  }

  /**
   * Migrate legacy custom questions to message scenarios (one-way).
   * POST /agent/{agent}/scenarios/migrate
   *
   * Pass `text` to persist the same briefing the user previewed in the
   * Switch-to-Scenarios modal. When omitted, the server regenerates the
   * briefing via the LLM.
   */
  migrateToScenarios(text?: string): Observable<any> {
    const body = text ? { text } : {};
    return this.http.post(`${getApiUrl()}agent/${this.primary.id}/scenarios/migrate`, body, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data));
  }

  /**
   * Generate a briefing text from a scenario title.
   * POST /agent/{agent}/scenarios/suggest-briefing
   */
  suggestBriefing(title: string): Observable<{ text: string }> {
    return this.http.post(`${getApiUrl()}agent/${this.primary.id}/scenarios/suggest-briefing`, { title }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data || { text: '' }));
  }

  /**
   * Preview the briefing the server would produce when migrating legacy custom
   * questions into the first scenario. Uses the agent's actual questions on the
   * server, so the preview matches what gets persisted.
   * POST /agent/{agent}/scenarios/suggest-migration-briefing
   */
  suggestMigrationBriefing(): Observable<{ text: string }> {
    return this.http.post(`${getApiUrl()}agent/${this.primary.id}/scenarios/suggest-migration-briefing`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data || { text: '' }));
  }

  uploadFile(file: File, name?: string, description?: string): Observable<string> {
    const formData: FormData = new FormData();
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('X-Business-ID', this.user.activeBusiness?.id);
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    return this.http.post(`${getApiUrl()}files`, formData, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data), tap((file) => this.files.push(file))) as Observable<string>;
  }

  deleteFile(id: string): Observable<any> {
    return this.http.delete(`${getApiUrl()}files/${id}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data), tap(() => this.files = this.files.filter(file => file.id !== id)));
  }

  updateBookingMessage(bookingMessage: Booking) {
    if (!bookingMessage.id) { delete bookingMessage.id; }
    return this.http.post(`${getApiUrl()}booking-messages`, bookingMessage, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
  }

  updateCalendarConfig(calendarConfig: { meetingTitle: string, meetingDuration: number, meetingEventType?: string | null }) {
    return this.updateAgentConfiguration({
      configuration: { calendarConfig: calendarConfig } as any
    }, true);
  }

  updateBlockedPhoneNumbers(phoneNumbers: PhoneNumber[]) {
    return this.http.post(`${getApiUrl()}phone-numbers/blocked/batch`, { phoneNumbers }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  toggleVerification(enabled: boolean, key: string): Observable<any> {
    return this.http.post(`${getApiUrl()}agent/${this.primary?.id}/verification/${key}`, { enabled }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  toggleOptInVerification(enabled: boolean): Observable<any> {
    return this.http.post(`${getApiUrl()}businesses/${this.user.activeBusiness?.id}/opt-in-verification`, { enabled }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  getVerification(): Observable<any> {
    return this.http.get(`${getApiUrl()}agent/${this.primary?.id}/verification`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }).pipe(map((response: any) => response?.data));
  }

  getSMSList(): Observable<any> {
    return this.http.get(`${getApiUrl()}agent/${this.primary?.id}/verification/sms`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }).pipe(map((response: any) => response?.data));
  }

  updateFileMetadata(fileId: string, name: string, description: string): Observable<any> {
    return this.http.post(
      `${getApiUrl()}files/${fileId}`,
      { name, description },
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(
      map((response: any) => response?.data),
      tap((updatedFile) => {
        const index = this.files.findIndex(f => f.id === fileId);
        if (index !== -1) {
          this.files[index] = { ...this.files[index], name, description };
        }
      })
    );
  }

  updateFileWithContent(fileId: string, file: File, name: string, description: string): Observable<any> {
    const formData: FormData = new FormData();
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('X-Business-ID', this.user.activeBusiness?.id);

    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);

    return this.http.post(
      `${getApiUrl()}files/${fileId}`,
      formData,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(
      map((response: any) => response?.data),
      tap((updatedFile) => {
        const index = this.files.findIndex(f => f.id === fileId);
        if (index !== -1) {
          this.files[index] = updatedFile;
        }
      })
    );
  }

  getCalendarEventQuestions(calendarReference: string, eventTypeId: string): Observable<any[]> {
    return this.http.get(`${getApiUrl()}apps/${calendarReference}/event-types/${eventTypeId}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response?.data?.customQuestions || []));
  }

  /**
   * Upload SMS widget avatar image.
   * POST /sms/avatar with multipart/form-data
   * Returns { avatar: string }
   */
  uploadSmsAvatar(file: File): Observable<{ avatar: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(
      `${getApiUrl()}sms/avatar`,
      formData,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(map((response: any) => response?.data));
  }

  /**
   * Enable SMS capabilities for the business phone number.
   * POST /businesses/{business}/phone-numbers/sms
   * Note: POST returns no response body - status is assumed to be 'in_progress' on success.
   */
  enableSmsCapabilities(): Observable<SmsCapabilitiesStatus> {
    return this.http.post(
      `${getApiUrl()}businesses/${this.user.activeBusiness?.id}/phone-numbers/sms`,
      {},
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(map(() => ({ status: 'in_progress' as const })));
  }

  /**
   * Get SMS capabilities status for the business phone number.
   * GET /businesses/{business}/phone-numbers/sms
   */
  getSmsCapabilitiesStatus(): Observable<SmsCapabilitiesStatus> {
    return this.http.get(
      `${getApiUrl()}businesses/${this.user.activeBusiness?.id}/phone-numbers/sms`,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(map((response: any) => this.normalizeSmsStatus(response?.data)));
  }

  /**
   * Normalize SMS status from API.
   * HTTP interceptor converts to camelCase, so "in-progress" becomes "inProgress".
   * We need to convert to our internal format: "in_progress"
   */
  private normalizeSmsStatus(data: any): SmsCapabilitiesStatus {
    if (!data) return { status: 'inactive' };

    // Map camelCase status from API to our snake_case format
    const statusMap: { [key: string]: SmsCapabilitiesStatus['status'] } = {
      'inactive': 'inactive',
      'inProgress': 'in_progress',
      'in_progress': 'in_progress',
      'in-progress': 'in_progress',
      'active': 'active',
      'failed': 'failed'
    };

    const status = statusMap[data.status] || 'inactive';

    return {
      ...data,
      status
    };
  }

  /**
   * Get scrape status for the business website.
   * GET /agents/{id}/scrape/status
   * Note: Backend endpoint not ready yet - returns default value until API is available.
   */
  getScrapeStatus(): Observable<ScrapeStatus | null> {
    return this.http.get(
      `${getApiUrl()}agents/${this.primary.id}/scrape/status`,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(
      map((response: any) => response?.data || null)
    );
  }

  /**
   * Get crawled URLs for the business website.
   * GET /agents/{id}/crawled-urls
   */
  getCrawledUrls(): Observable<CrawledUrl[]> {
    return this.http.get(
      `${getApiUrl()}agents/${this.primary.id}/crawled-urls`,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(
      map((response: any) => response?.data || [])
    );
  }

  /**
   * Update crawled URL active states.
   * PATCH /agents/{id}/crawled-urls
   */
  updateCrawledUrls(crawledUrls: CrawledUrl[]): Observable<any> {
    return this.http.patch(
      `${getApiUrl()}agents/${this.primary.id}/crawled-urls`,
      { crawledUrls },
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    );
  }
}

export interface SmsCapabilitiesStatus {
  status: 'inactive' | 'in_progress' | 'active' | 'failed';
  phoneNumber?: string;
  message?: string;
}
